import { formatMetricValue, type ClientStatusDto, type MetricUnit } from '@citilight-monitor/contracts';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { HEALTH_CHIP, HEALTH_LABELS, HEALTH_ORDER, metricStatus } from '../../lib/metrics';
import { formatDateTime, formatRelative } from '../../lib/time';
import { Meter, StatusChip, Table } from '../atoms';
import { MetricValue } from '../molecules';

const RESOURCE_COLUMNS = [
  { key: 'cpu_percent', label: 'CPU' },
  { key: 'memory_percent', label: 'Memory' },
  { key: 'disk_percent', label: 'Disk' },
] as const;

type SortKey = 'status' | 'name' | 'lastPoll' | (typeof RESOURCE_COLUMNS)[number]['key'];
type Sort = { key: SortKey; dir: 1 | -1 };

function sortValue(status: ClientStatusDto, key: SortKey): number | string {
  switch (key) {
    case 'status':
      return HEALTH_ORDER[status.health];
    case 'name':
      return status.client.name.toLowerCase();
    case 'lastPoll':
      return status.latest ? Date.parse(status.latest.timestamp) : 0;
    default:
      return status.latest?.metrics[key] ?? -1;
  }
}

/** Every client with its current state. Problems first by default; headers sort; rows open the client page. */
export function ClientsTable({ clients }: { clients: ClientStatusDto[] }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<Sort>({ key: 'status', dir: 1 });

  const sorted = [...clients].sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    const order = va < vb ? -1 : va > vb ? 1 : 0;
    return order * sort.dir || a.client.name.localeCompare(b.client.name);
  });

  function header(key: SortKey, label: string, className?: string) {
    const active = sort.key === key;
    const Icon = !active ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
    // Numbers and recency read best biggest-first on the first click.
    const firstDir = key === 'name' || key === 'status' ? 1 : -1;
    return (
      <th scope="col" className={className} aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
        <button
          type="button"
          onClick={() => setSort(active ? { key, dir: sort.dir === 1 ? -1 : 1 } : { key, dir: firstDir })}
          className={cn('inline-flex cursor-pointer items-center gap-1 hover:text-fg', active && 'text-fg')}
        >
          {label}
          <Icon size={12} className={cn(!active && 'opacity-40')} aria-hidden />
        </button>
      </th>
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          {header('name', 'Client')}
          {header('status', 'Status')}
          {RESOURCE_COLUMNS.map((c) => header(c.key, c.label))}
          <th scope="col">Application</th>
          {header('lastPoll', 'Last poll')}
        </tr>
      </thead>
      <tbody>
        {sorted.map((status) => {
          const { client, latest } = status;
          const health = HEALTH_LABELS[status.health];
          const appMetrics = client.metrics.filter((m) => m.source === 'app' && m.unit !== 'state');
          return (
            <tr key={client.id} className="is-link" onClick={() => navigate(`/clients/${client.id}`)}>
              <td>
                <Link to={`/clients/${client.id}`} className="font-semibold text-fg no-underline hover:text-accent" onClick={(e) => e.stopPropagation()}>
                  {client.name}
                </Link>
                <div className="font-mono text-xs text-fg-muted">{client.id}</div>
              </td>
              <td>
                <StatusChip tone={HEALTH_CHIP[status.health].tone} label={health.label} />
              </td>
              {RESOURCE_COLUMNS.map((c) => (
                <td key={c.key}>
                  <ResourceCell status={status} metricKey={c.key} />
                </td>
              ))}
              <td>
                {appMetrics.length === 0 ? (
                  <span className="text-fg-muted">—</span>
                ) : (
                  appMetrics.map((m) => (
                    <div key={m.key} className="flex items-baseline gap-2">
                      <span className="text-xs text-fg-muted">{m.label}</span>
                      <LatestValue status={status} metricKey={m.key} unit={m.unit} />
                    </div>
                  ))
                )}
              </td>
              <td className="font-mono text-xs text-fg-secondary" title={latest ? formatDateTime(latest.timestamp) : undefined}>
                {latest ? formatRelative(latest.timestamp) : 'Never'}
              </td>
            </tr>
          );
        })}
        {!sorted.length && (
          <tr>
            <td colSpan={7} className="py-8 text-center text-fg-muted">
              No clients match this filter.
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
}

/** Percentage over a meter; the tick marks the alert threshold. */
function ResourceCell({ status, metricKey }: { status: ClientStatusDto; metricKey: string }) {
  const value = status.latest?.metrics[metricKey];
  if (!status.client.metrics.some((m) => m.key === metricKey) || value === undefined) {
    return <span className="text-fg-muted">—</span>;
  }
  const rule = metricStatus(status, metricKey);
  return (
    <div className="flex w-[96px] flex-col gap-1.5">
      <LatestValue status={status} metricKey={metricKey} unit="percent" />
      <Meter percent={value} threshold={rule?.rule.threshold} tone={rule?.tone} />
    </div>
  );
}

function LatestValue({ status, metricKey, unit }: { status: ClientStatusDto; metricKey: string; unit: MetricUnit }) {
  if (!status.client.metrics.some((m) => m.key === metricKey)) return <span className="text-fg-muted">—</span>;
  const rule = metricStatus(status, metricKey);
  return (
    <MetricValue
      value={formatMetricValue(unit, status.latest?.metrics[metricKey])}
      alert={rule && rule.tone !== 'good' ? rule : undefined}
    />
  );
}
